import { useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ExternalLink, MoreHorizontal, Radio, X } from 'lucide-react';
import { useIntegrations, useDisconnectIntegration } from '@/hooks';
import { integrationService } from '@/services';
import { toast } from '@/components/ui/sonner';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DialogFooter } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { confirmDisconnect } from './disconnect-confirm.store';
import { RECHARGE_API_TOKEN_GUIDE_URL, RECHARGE_TOKEN_PERMISSIONS, describeMissingWriteScopes, readMissingScopes } from '../recharge';
import type { Integration } from '../types';

const rechargeFormSchema = z.object({
	apiKey: z.string().min(1, 'API key is required').max(512, 'API key is too long')
});

type RechargeFormData = z.infer<typeof rechargeFormSchema>;

const ScopeChip = ({ scope, isMissing }: { scope: string; isMissing: boolean }) => (
	<code
		className={cn(
			'inline-flex items-center gap-1 rounded px-1.5 py-0.5 font-mono text-xs',
			isMissing ? 'bg-destructive/10 text-destructive' : 'bg-muted text-foreground'
		)}
	>
		{isMissing && <X className='size-3' aria-label='missing' />}
		{scope}
	</code>
);

const GuideStep = ({ step, children }: { step: number; children: ReactNode }) => (
	<li className='flex gap-2'>
		<span className='flex size-4 shrink-0 items-center justify-center rounded-full bg-muted text-[10px] font-medium'>{step}</span>
		<div className='flex flex-1 flex-col gap-1.5 text-xs'>{children}</div>
	</li>
);

/** How to create the API token in Recharge, with the permissions a rejected key lacked marked. */
const ApiTokenGuide = ({ missingScopes }: { missingScopes: string[] }) => (
	<div className='flex flex-col gap-3 rounded-lg border p-3'>
		<div className='flex items-center justify-between'>
			<p className='text-xs font-medium'>How to get your API key</p>
			<a href={RECHARGE_API_TOKEN_GUIDE_URL} target='_blank' rel='noreferrer' className='inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground'>
				Recharge guide <ExternalLink className='size-3' />
			</a>
		</div>
		<ol className='flex flex-col gap-2.5'>
			<GuideStep step={1}>
				<span>
					In the Recharge merchant portal, go to <strong className='font-medium'>Tools &amp; apps → API tokens</strong>.
				</span>
				<span className='text-[11px] text-muted-foreground'>Only the store owner sees this page unless they've given staff access.</span>
			</GuideStep>
			<GuideStep step={2}>
				<span>
					Click <strong className='font-medium'>Create an API Token</strong>, and enter a name (Kim) and a contact email.
				</span>
			</GuideStep>
			<GuideStep step={3}>
				<span>Set these permissions, then click <strong className='font-medium'>Save</strong>:</span>
				<div className='flex flex-col divide-y rounded-md border'>
					{RECHARGE_TOKEN_PERMISSIONS.map(({ resource, access, scopes }) => (
						<div key={resource} className='flex flex-wrap items-center justify-between gap-1.5 px-2 py-1.5'>
							<span className='flex items-center gap-1.5'>
								<span className='font-medium'>{resource}</span>
								<span className='text-muted-foreground'>{access}</span>
							</span>
							<span className='flex flex-wrap gap-1'>
								{scopes.map((scope) => (
									<ScopeChip key={scope} scope={scope} isMissing={missingScopes.includes(scope)} />
								))}
							</span>
						</div>
					))}
				</div>
				<span className='text-[11px] text-muted-foreground'>
					Without the read permissions the key is rejected. Without a write permission it connects, but the actions it covers won't run.
				</span>
			</GuideStep>
			<GuideStep step={4}>
				<span>Copy the token and paste it above.</span>
			</GuideStep>
		</ol>
	</div>
);

export const RechargeContent = ({ integration }: { integration: Integration }) => {
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [rejectedForScopes, setRejectedForScopes] = useState<string[]>([]);
	const disconnectMutation = useDisconnectIntegration();
	const { refetch } = useIntegrations();

	const form = useForm<RechargeFormData>({
		resolver: zodResolver(rechargeFormSchema),
		defaultValues: { apiKey: '' }
	});

	const handleConnect = async (data: RechargeFormData) => {
		setIsSubmitting(true);
		setRejectedForScopes([]);
		try {
			const { missingScopes = [] } = await integrationService.connectRechargeIntegration(data.apiKey);
			await refetch();
			const missingWriteWarning = describeMissingWriteScopes(missingScopes);
			if (missingWriteWarning)
				toast.warning(`Recharge connected, but these actions won't run until you add the permissions: ${missingWriteWarning}`, {
					duration: 15000
				});
			else toast.success('Recharge connected');
		} catch (err) {
			console.error('Recharge connect failed', err);
			const errorResponse = (err as { response?: { data?: { error?: string } } })?.response?.data;
			setRejectedForScopes(readMissingScopes(err));
			toast.error(errorResponse?.error ?? 'Failed to connect Recharge. Check your API key and try again.');
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleDisconnect = async () => {
		if (disconnectMutation.isPending) return;
		if (!(await confirmDisconnect(integration.name))) return;
		disconnectMutation.mutate(integration.id);
	};

	if (integration.isConnected) {
		return (
			<div className='flex flex-col gap-4 px-5'>
				<div className='flex items-start justify-between'>
					<div className='flex flex-col gap-0.5'>
						<h3 className='text-sm'>Connected {integration.name} Agent</h3>
						<p className='text-[10px] italic text-muted-foreground'>Manage or relink your {integration.name} connection.</p>
					</div>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant='ghost' size='icon-sm'>
								<MoreHorizontal className='size-4' />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align='end'>
							<DropdownMenuItem variant='destructive' onClick={handleDisconnect} disabled={disconnectMutation.isPending}>
								{disconnectMutation.isPending ? 'Disconnecting…' : 'Disconnect'}
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>

				<div className='flex flex-col gap-2 p-3 bg-muted rounded-lg'>
					<Label className='text-xs text-muted-foreground'>Status</Label>
					<span className='text-sm font-medium'>Connected</span>
				</div>
			</div>
		);
	}

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(handleConnect)} className='flex flex-col h-full'>
				<div className='flex flex-col gap-4 px-5 flex-1'>
					<div className='flex flex-col gap-1'>
						<h3 className='text-sm font-semibold'>Link a {integration.name} Account</h3>
						<p className='text-sm text-muted-foreground'>Enter your Recharge API key to activate the integration.</p>
					</div>

					<FormField
						control={form.control}
						name='apiKey'
						render={({ field }) => (
							<FormItem>
								<FormLabel>API Key</FormLabel>
								<FormControl>
									<Input type='password' autoComplete='new-password' placeholder='Enter your Recharge API key' {...field} />
								</FormControl>
																<FormMessage />
							</FormItem>
						)}
					/>

					{rejectedForScopes.length > 0 && (
						<div role='alert' className='rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-xs text-destructive'>
							This key is missing {rejectedForScopes.length} permission{rejectedForScopes.length === 1 ? '' : 's'}:{' '}
							{rejectedForScopes.join(', ')}. Add them to the token in Recharge and connect again.
						</div>
					)}

					<ApiTokenGuide missingScopes={rejectedForScopes} />
				</div>

				<DialogFooter className='px-5 pb-5 pt-4'>
					<Button type='submit' className='w-full' size={'lg'} disabled={isSubmitting}>
						<Radio className='size-4' />
						{isSubmitting ? 'Connecting…' : 'Connect'}
					</Button>
				</DialogFooter>
			</form>
		</Form>
	);
};
